import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import React, { ForwardedRef, useEffect, useState } from "react"
import { BaseInputProps, Input } from "../Input/Input"
import { clamp } from "ramda"
import { classNames } from "../../../utils/classNames"
import { formatPrice, parseNumber } from "@figurepos/fgr-lib-business-logic/lib/formatters/price_formatters"
import { Input as AntInput } from "antd"

export type PriceInputProps = Omit<BaseInputProps, "value" | "prefix" | "onChange"> & {
    min?: number
    max?: number
    onChange?: (value: Maybe<number>) => void
    value?: Maybe<number>
}

/** PriceInput is a component that allows to input price values. It accepts a number and formats it to a price string. */
export const PriceInput = React.forwardRef(
    (
        {
            min = 0,
            max = 9999,
            value,
            onChange: onChangeProp,
            onBlur: onBlurProp,
            className,
            ...restProps
        }: PriceInputProps,
        ref: ForwardedRef<AntInput>
    ) => {
        const [inputValue, setInputValue] = useState("")

        useEffect(() => {
            setInputValue(value != null ? formatPrice(value) : "")
        }, [value])

        const onChange = (e: any) => {
            setInputValue(e.target.value)
        }

        const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            onBlurProp?.(e)

            const parsedValue = inputValue ? clampValue(parseNumber(inputValue, 2)) : null

            // Fire 'onChange' prop with raw parsed number
            onChangeProp?.(parsedValue)

            // Change the actual displayed input value to show formatted number
            setInputValue(parsedValue ? formatPrice(parsedValue) : "")
        }

        const clampValue = (val: number) => clamp(min * 100, max * 100, val)

        return (
            <Input
                onChange={onChange}
                onBlur={onBlur}
                placeholder="$0.00"
                className={classNames(className)}
                value={inputValue || ""}
                ref={ref}
                {...restProps}
            />
        )
    }
)

PriceInput.displayName = "PriceInput"
